// generic data

import { Indexer } from "@/common/types/indexer";

export interface MinMaxValues {
  min: number;
  max: number;
}

export type MinMaxExtendedValues = {
  min: number;
  max: Record<string, number>;
}

export interface IProductInfo extends JsonData {
  id: number;
  name: string;
  code: string;
  lineOfBusinessId: number;
  minAgeDays: number;
  minAge: number;
  maxAge: number;
  minPayorAge: number;
  maxPayorAge: number;
  adultAgeThreshold: number;
  maxTotalCoverageAmountPerInsured: number;
  maxTotalYearlyPremiumAmountForDisabilityWaiverOfPremium: number;
  productVariants: IProductVariant[];
  plans: IPlan[];
  criticalIllnessRiderPremiumTypes: ICriticalIllnessRiderPremiumType[];
  minMonthlyPremiumAmount: number;
  minYearlyPremiumAmount: number;
  juvenileAdultMinMax: JuvenileAdultMinMax;
  ratingPercentages: RatingPercentage[];
  ratingPercentagesMemberships: RatingPercentagesMembership[];
  jsonData: unknown;
}

export type RatingPercentage = {
  id: number;
  value: number;
  name: string;
  displayOrder: number;
}

export type RatingPercentagesMembership = {
  planId: number | null;
  percentageId: number;
}

export interface IProductVariant {
  id: number;
  code: string;
  name: string;
  productId: number;
  isJoint: boolean;
  minNumberOfLives: number;
  maxNumberOfLives: number;
  minAge: number;
  maxAge: number;
  planIds: number[];
}

export interface IPlan {
  id: number;
  code: string;
  displayOrder: number;
  name: string;
  shortName: string;
  planType: PlanType;
  planKind: PlanKind;
  isActive: boolean;
  uiHint: UiHint | null;
  availabilitiesForMinMaxLives: IAvailabilityForMinMaxLive[]; // for every product variant indicates for what (by index of the insured in the policy) insured the plan is availble to
  planAgeFaceAmountRanges: IPlanAgeFaceAmountRange[];
  inclusiveExclusiveWithPlans: InclusiveExclusiveWithPlan[];
}

export interface InclusiveExclusiveWithPlan {
  productVariantId: number;
  withPlanId: number;
  inclusiveExclusive: boolean;
}

export interface IAvailabilityForMinMaxLive {
  productVariantId: number;
  isAvailableForLiveMin: number | null;
  isAvailableForLiveMax: number | null;
}

export interface IPlanAgeFaceAmountRange {
  productVariantId: number;
  withPlanId: number | null;
  minAgeDays: number;
  minAge: number;
  maxAge: number;
  minFaceAmount: number;
  maxFaceAmount: number;
}

export interface ICriticalIllnessRiderPremiumType {
  id: number;
  name: string;
  productId: number;
  code: string;
  collectExpiryAge: boolean;
  disableExpiryAge: boolean;
  minAgeDays: number | null;
  minAge: number;
  maxAge: number;
  minExpiryAge: number;
  maxExpiryAge: number;
}

export enum UiHint {
  FaceAmount = 0,
  Cpr = 1,
  Cir = 2,
  PayorWaiver = 3,
  CiOptions = 4,
  Cit = 5
}

export enum PlanType {
  BasePlan = 0,
  Rider = 1
}

export enum PlanKind {
  Insured = 0,
  Payor = 1,
  Policy = 2
}

export interface JuvenileAdultMinMax {
  juvenileMin: number;
  juvenileMax: number;
  adultMin: number;
  adultMax: number;
}

// mapped data

export interface IMappedProductInfo {
  productVariants: Indexer<IProductVariant>; // productVariants[productVariantId] --> IProductVariant
  plans: Indexer<IPlan>; // plans[planId] --> IPlan
  planMinMaxLives: Indexer<Indexer<IAvailabilityForMinMaxLive>>; // planMinMaxLives[planId][productVariantId] --> IPlanMinMaxLive
  planAgeFaceAmountRanges: Indexer<Indexer<IPlanAgeFaceAmountRange[]>>; // planAgeFaceAmountRanges[planId][productVariantId] --> IPlanAgeFaceAmountRanges[]
  criticalIllnessRiderPremiumTypes: Indexer<ICriticalIllnessRiderPremiumType>;

}

export interface JsonData {
  jsonData: unknown;
}

export interface CodesWaiverOfPremium {
  codesWaiverOfPremium: string[];
}
